import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface ChamaSettings {
  id: string;
  name: string;
  description?: string;
  contributionAmount: number;
  contributionFrequency: string;
  maxMembers: number;
  type: string;
  category: string;
  isPublic: boolean;
  requireApproval: boolean;
  allowAnonymousContributions: boolean;
  meetingReminders: boolean;
  contributionReminders: boolean;
  welfareNotifications: boolean;
  loanNotifications: boolean;
}

const ChamaSettings: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [settings, setSettings] = useState<ChamaSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (chamaId) {
      loadSettings();
    }
  }, [chamaId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaSettings: No auth token found');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setSettings(data.data);
        } else {
          // Set default settings if none exist
          setSettings({
            id: chamaId || '',
            name: '',
            description: '',
            contributionAmount: 0,
            contributionFrequency: 'monthly',
            maxMembers: 50,
            type: 'community',
            category: 'contribution',
            isPublic: true,
            requireApproval: false,
            allowAnonymousContributions: false,
            meetingReminders: true,
            contributionReminders: true,
            welfareNotifications: true,
            loanNotifications: true,
          });
        }
      } else {
        console.error('❌ ChamaSettings: Failed to load settings');
      }
    } catch (error) {
      console.error('❌ ChamaSettings: Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Settings saved successfully!');
        } else {
          alert(`Failed to save settings: ${data.error || 'Unknown error'}`);
        }
      } else {
        alert('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Save settings error:', error);
      alert('Network error: Unable to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof ChamaSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const renderGeneralTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.settingsSection}>
        <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Basic Information</h3>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Chama Name</label>
          <input
            type="text"
            value={settings?.name || ''}
            onChange={(e) => updateSetting('name', e.target.value)}
            style={{ ...styles.input, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Description</label>
          <textarea
            value={settings?.description || ''}
            onChange={(e) => updateSetting('description', e.target.value)}
            rows={3}
            style={{ ...styles.textarea, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Category</label>
          <select
            value={settings?.category || 'contribution'}
            onChange={(e) => updateSetting('category', e.target.value)}
            style={{ ...styles.select, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          >
            <option value="contribution">Contribution Group</option>
            <option value="investment">Investment Club</option>
            <option value="welfare">Welfare Group</option>
            <option value="business">Business Group</option>
            <option value="merry-go-round">Merry-Go-Round</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Type</label>
          <select
            value={settings?.type || 'community'}
            onChange={(e) => updateSetting('type', e.target.value)}
            style={{ ...styles.select, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          >
            <option value="community">Community</option>
            <option value="family">Family</option>
            <option value="friends">Friends</option>
            <option value="workplace">Workplace</option>
            <option value="religious">Religious</option>
          </select>
        </div>
      </div>

      <div style={styles.settingsSection}>
        <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Contribution Settings</h3>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Contribution Amount (KES)</label>
          <input
            type="number"
            value={settings?.contributionAmount || 0}
            onChange={(e) => updateSetting('contributionAmount', parseFloat(e.target.value) || 0)}
            style={{ ...styles.input, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Contribution Frequency</label>
          <select
            value={settings?.contributionFrequency || 'monthly'}
            onChange={(e) => updateSetting('contributionFrequency', e.target.value)}
            style={{ ...styles.select, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={{ ...styles.label, color: colors.text }}>Maximum Members</label>
          <input
            type="number"
            value={settings?.maxMembers || 50}
            onChange={(e) => updateSetting('maxMembers', parseInt(e.target.value) || 50)}
            min="1"
            max="1000"
            style={{ ...styles.input, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
          />
        </div>
      </div>

      <div style={styles.settingsSection}>
        <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Privacy & Access</h3>

        <div style={styles.toggleGroup}>
          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Public Chama</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                Allow anyone to find and join this chama
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.isPublic || false}
                onChange={(e) => updateSetting('isPublic', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>

          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Require Approval</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                New members need approval before joining
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.requireApproval || false}
                onChange={(e) => updateSetting('requireApproval', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>

          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Anonymous Contributions</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                Allow members to contribute anonymously
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.allowAnonymousContributions || false}
                onChange={(e) => updateSetting('allowAnonymousContributions', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.settingsSection}>
        <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Notification Preferences</h3>
        <p style={{ ...styles.sectionDescription, color: colors.textSecondary }}>
          Choose which notifications you want to receive for this chama.
        </p>

        <div style={styles.toggleGroup}>
          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Meeting Reminders</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                Get notified about upcoming meetings and deadlines
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.meetingReminders ?? true}
                onChange={(e) => updateSetting('meetingReminders', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>

          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Contribution Reminders</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                Get reminded about contribution deadlines
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.contributionReminders ?? true}
                onChange={(e) => updateSetting('contributionReminders', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>

          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Welfare Notifications</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                Get notified about welfare proposals and updates
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.welfareNotifications ?? true}
                onChange={(e) => updateSetting('welfareNotifications', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>

          <div style={styles.toggleItem}>
            <div style={styles.toggleContent}>
              <div style={{ ...styles.toggleLabel, color: colors.text }}>Loan Notifications</div>
              <div style={{ ...styles.toggleDescription, color: colors.textSecondary }}>
                Get notified about loan applications and updates
              </div>
            </div>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={settings?.loanNotifications ?? true}
                onChange={(e) => updateSetting('loanNotifications', e.target.checked)}
              />
              <span style={styles.toggleSlider}></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Chama Settings"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            <button
              onClick={() => setActiveTab('general')}
              style={{
                ...styles.tabButton,
                backgroundColor: activeTab === 'general' ? colors.primary : 'transparent',
                color: activeTab === 'general' ? 'white' : colors.text,
              }}
            >
              ⚙️ General
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              style={{
                ...styles.tabButton,
                backgroundColor: activeTab === 'notifications' ? colors.primary : 'transparent',
                color: activeTab === 'notifications' ? 'white' : colors.text,
              }}
            >
              🔔 Notifications
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'general' ? renderGeneralTab() : renderNotificationsTab()}

          {/* Save Button */}
          <div style={styles.saveContainer}>
            <button
              onClick={saveSettings}
              disabled={saving}
              style={{
                ...styles.saveButton,
                backgroundColor: saving ? colors.textSecondary : colors.primary,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  tabContainer: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  tabButton: {
    flex: 1,
    padding: `${spacing.md} ${spacing.lg}`,
    borderRadius: borderRadius.lg,
    border: 'none',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  tabContent: {
    // Container for tab content
  },
  settingsSection: {
    marginBottom: spacing.xl,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.md,
  },
  sectionDescription: {
    fontSize: typography.fontSize.sm,
    margin: 0,
    marginBottom: spacing.lg,
  },
  formGroup: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
    display: 'block',
  },
  input: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
    resize: 'vertical',
    minHeight: 80,
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  toggleItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: borderRadius.md,
  },
  toggleContent: {
    flex: 1,
  },
  toggleLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs / 2,
  },
  toggleDescription: {
    fontSize: typography.fontSize.sm,
  },
  toggle: {
    position: 'relative',
    display: 'inline-block',
    width: 50,
    height: 24,
  },
  toggleSlider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#ccc',
    transition: '.4s',
    borderRadius: 24,
  },
  toggleSliderBefore: {
    position: 'absolute',
    content: '""',
    height: 18,
    width: 18,
    left: 3,
    bottom: 3,
    backgroundColor: 'white',
    transition: '.4s',
    borderRadius: '50%',
  },
  saveContainer: {
    marginTop: spacing.xl,
    paddingTop: spacing.lg,
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  saveButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
};

export default ChamaSettings;